import React, {useEffect, useState} from 'react'
import {IMOBywatelIdentityMethodSectionProps} from './MObywatelIdentityPageProps'
import clsx from "clsx";

import {getAppLinkBasedOnOS} from './helpers'
import {useMObywatelPageLogic} from './hooks/useMObywatelPageLogic'
import useIsMobile from '../../hooks/useIsMobile'
import useTextContents from '../../hooks/useTextContents'
import {AppLinks} from '../../environment/app-links'
import {apphistory} from '../../store'

import {AppList} from "../../components/atoms/AppList/AppList";
import {AppListItem} from "../../components/atoms/AppListItem/AppListItem";
import PageScreen from "../../components/organisms/Page/PageScreen";
import Paragraph from "../../components/atoms/Paragraph/Paragraph";
import AppQrCode from "../../components/molecules/AppQrCode/AppQrCode";
import AppLink from "../../components/atoms/AppLink/AppLink";
import AppCode from "../../components/molecules/AppCode/AppCode";
import {STORAGEKEYS} from "../../common/keys";
import LoanOfferSummaryPanel from '../../components/organisms/LoanOfferSummaryPanel/LoanOfferSummaryPanel'
import {APP_COLOR, APP_FONT_SIZE} from '../../components/atoms/AtomsGlobalProps'

import './MObywatelIdentityPageStyles.scss'


/**
 * Komponent zwracający tekst z linkiem do pobrania aplikacji mObywatel dostosowanym do konkretnego urządzenia.
 * (Sklep Play - na Androidzie, Apple Store na IOS)
 */
const GetMObywatelAppLink = (): JSX.Element => {
    const link = getAppLinkBasedOnOS();
    const linkString = link !== undefined ? link : "";
    return (
        <div>
            <Paragraph align={"center"}
                       size={APP_FONT_SIZE.MEDIUM}
                       color={APP_COLOR.GRAY}
                       style={{marginBottom: '5px'}}>
                Nie masz aplikacji mObywatel? Masz nieaktualną wersje?
            </Paragraph>

            <Paragraph align={"center"}>
                <AppLink size={APP_FONT_SIZE.MEDIUM}
                         href={linkString}
                         externalCard>Pobierz lub zaktualizuj za darmo.</AppLink>
            </Paragraph>
        </div>
    )
}


const MObywatelIdentitySectionDesktop = (props: IMOBywatelIdentityMethodSectionProps): JSX.Element => {
    const BASE_BUTTON_TEXT = "Skopiuj kod"
    const [buttonText, setButtonText] = useState<string>(BASE_BUTTON_TEXT)
    const {
        currentMObywatelCodeExpiredTime,
        mObywatelCurrentlyActiveCode,
        regenerateMObywatelCode,
        textContents,
        handleOnPrevButtonClick
    } = props


    return (
        <PageScreen title={{title: textContents('mObywatelIdentityPage.desktop.header'), line: true}}
                    showBackButton={true}
                    panel={<LoanOfferSummaryPanel readOnly={true} loanVariant='selectedOffer'/>}
                    onClickBack={handleOnPrevButtonClick}>

            <div className={"page-gap"}>
                <Paragraph align={"justify"}>
                    {textContents('mObywatelIdentityPage.desktop.mainContent')}
                </Paragraph>
                <AppList>
                    <AppListItem html={textContents('mObywatelIdentityPage.desktop.instruction.step1')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.desktop.instruction.step2')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.desktop.instruction.step3')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.desktop.instruction.step4')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.desktop.instruction.step5')}/>
                </AppList>
                <AppQrCode
                    className={"center"}
                    qrCode={mObywatelCurrentlyActiveCode}
                    validDate={currentMObywatelCodeExpiredTime}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => regenerateMObywatelCode(false)}
                    textContent={textContents}/>

                <GetMObywatelAppLink/>
            </div>
        </PageScreen>
    )
}


const MObywatelIdentitySectionMobile = (props: IMOBywatelIdentityMethodSectionProps): JSX.Element => {
    const {
        currentMObywatelCodeExpiredTime,
        mObywatelCurrentlyActiveCode,
        regenerateMObywatelCode,
        textContents,
        handleOnPrevButtonClick
    } = props

    return (

        <PageScreen
            title={{title: textContents('mObywatelIdentityPage.mobile.header'), line: true}}
            showBackButton={true} onClickBack={handleOnPrevButtonClick}
            panel={<LoanOfferSummaryPanel readOnly={true} loanVariant='selectedOffer'/>}
        >
            <div className={"page-gap"}>

                <Paragraph align={"justify"}>
                    {textContents('mObywatelIdentityPage.mobile.mainContent')}
                </Paragraph>


                <AppList>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step1')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step2')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step3')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step4')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step5')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step6')}/>
                    <AppListItem html={textContents('mObywatelIdentityPage.mobile.instruction.step7')}/>
                </AppList>

                <AppCode
                    style={{marginBottom: '1.12rem'}}
                    className={"center"}
                    code={mObywatelCurrentlyActiveCode}
                    validDate={currentMObywatelCodeExpiredTime}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => regenerateMObywatelCode(false)}
                    textContent={textContents}/>
                <GetMObywatelAppLink/>
            </div>
        </PageScreen>

    )
}


const MObywatelIdentityPage = (): JSX.Element => {
    const {
        currentMObywatelCodeExpiredTime,
        isMobywatelCodeActive,
        regenerateMObywatelCode,
        mObywatelCurrentlyActiveCode
    } = useMObywatelPageLogic()

    const isMobileDevice = useIsMobile()


    const textContents = useTextContents()
    const [hideMobileScreen, setHideMobileScreen] = useState(true);

    const handleOnPrevButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault()
        apphistory.push(AppLinks.clientChoiceManualVerification)
    }

    useEffect(() => {
        if (!hideMobileScreen) {
            return;
        }
        if (!isMobileDevice) {
            setHideMobileScreen(false)
            return;
        }
        const firstRenderingAsString = sessionStorage.getItem(STORAGEKEYS.MOBYWATEL_IS_FIRST_RENDER);
        const firstRendering = firstRenderingAsString == null || firstRenderingAsString === 'true'

        if (!firstRendering) {
            setHideMobileScreen(false)
            return;
        }
        if (!mObywatelCurrentlyActiveCode) return;

        const deepLink = `mobywatel://app/institutions?qrCode=${encodeURIComponent(
            mObywatelCurrentlyActiveCode
        )}`;
        window.location.href = deepLink;

        const timer = window.setTimeout(() => {
            setHideMobileScreen(false);
        }, 1500);

        return () => {
            window.clearTimeout(timer);
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mObywatelCurrentlyActiveCode])


    return (
        <>
            {
                isMobileDevice ?
                    <MObywatelIdentitySectionMobile
                        textContents={textContents}
                        regenerateMObywatelCode={regenerateMObywatelCode}
                        mObywatelCurrentlyActiveCode={mObywatelCurrentlyActiveCode}

                        isMobywatelCodeActive={isMobywatelCodeActive}
                        currentMObywatelCodeExpiredTime={currentMObywatelCodeExpiredTime}
                        handleOnPrevButtonClick={handleOnPrevButtonClick}
                    />
                    :
                    <MObywatelIdentitySectionDesktop
                        textContents={textContents}
                        regenerateMObywatelCode={regenerateMObywatelCode}
                        mObywatelCurrentlyActiveCode={mObywatelCurrentlyActiveCode}
                        isMobywatelCodeActive={isMobywatelCodeActive}
                        currentMObywatelCodeExpiredTime={currentMObywatelCodeExpiredTime}
                        handleOnPrevButtonClick={handleOnPrevButtonClick}
                    />
            }
        </>
    )
}

export default MObywatelIdentityPage